/* This composable contains custom AgGrid cell renderers, value getters and formatters,
and other useful helper functions for the product modelling grid.
*/
import { get, inRange, isEmpty, unset } from 'lodash';
import { useInternationalization } from '@/composables/use-internationalization';
import agGridUtils from '@/utils/ag-grid-utils';

function calcSalesConfidenceScoreColour(score, clusterId) {
  const scsColours = {
    RED: 'red',
    ORANGE: 'orange',
    GREEN: 'green',
    GREY: 'grey',
  };

  if (inRange(score, 0.33, 0.66)) return scsColours.RED;

  if (clusterId) {
    if (inRange(score, 0.66, 0.9)) return scsColours.ORANGE;
    if (score >= 0.9) return scsColours.GREEN;
  } else {
    if (inRange(score, 0.66, 1)) return scsColours.ORANGE;
    if (score >= 1) return scsColours.GREEN;
  }
  return scsColours.GREY;
}

export const useProductModellingGridHelpers = () => {
  const { t } = useInternationalization();

  const overrideValueFormatter = params => {
    return ![null, undefined, ''].includes(params.value)
      ? agGridUtils.formatters.roundFormatter(params)
      : params.value;
  };

  const salesConfidenceScoreRenderer = (params, clusterId) => {
    const path = clusterId
      ? `data.${clusterId}.salesConfidenceScorePerformancePeriod`
      : 'data.salesConfidenceScorePerformancePeriod';
    const score = get(params, path, 0);
    const iconColour = calcSalesConfidenceScoreColour(score, clusterId);

    const span = document.createElement('span');
    span.classList.add('circle-icon', `circle-icon--${iconColour}`, 'mr-2');

    const scoreText = document.createElement('span');
    scoreText.textContent = score.toFixed(1); // Format score to 1 decimal place

    const container = document.createElement('div');
    container.classList.add('d-flex', 'align-center');
    container.appendChild(span);
    container.appendChild(scoreText);

    return container;
  };

  const tooltipValueGetter = params => {
    const errors = [];
    if (agGridUtils.validations.valueIsNotNumericPermissive(params))
      errors.push(t('validationErrors.number'));
    if (agGridUtils.validations.valueIsLessOrEqual(params.value, 0))
      errors.push(t('validationErrors.numberMustBeBiggerThan', [0]));

    if (agGridUtils.validations.valueIsNegative(params))
      errors.push(t('validationErrors.nonNegative'));
    // TooltipFeature added to cell if value "exists" (not null and not empty string) https://github.com/ag-grid/ag-grid/blob/v23.2.1/community-modules/core/src/ts/rendering/cellComp.ts#L221
    // TooltipFeature.showTooltip checks with !tooltip if value is showable https://github.com/ag-grid/ag-grid/blob/v23.2.1/community-modules/core/src/ts/widgets/tooltipFeature.ts#L171
    return errors.join('\n');
  };

  const unsetCurrentStateDataDiff = (obj, path) => {
    // Recursively unsets empty objects along a nested path.
    unset(obj, path);

    // If no parent path exists, we're done
    if (!path.includes('.')) return obj;

    // Get the parent path
    const parentPath = path.substring(0, path.lastIndexOf('.'));

    // Check if parent is now empty
    if (isEmpty(get(obj, parentPath))) {
      // Recurse to prune the parent
      return unsetCurrentStateDataDiff(obj, parentPath);
    }

    return obj;
  };

  return {
    overrideValueFormatter,
    salesConfidenceScoreRenderer,
    tooltipValueGetter,
    unsetCurrentStateDataDiff,
  };
};
